import { Select } from '@ardoq/select';
import { DeleteIcon } from '@ardoq/icons';
import { GhostButton, DragButton } from '@ardoq/button';
import { isNil, noop } from 'lodash';
import { useFormattingContext } from '../useFormattingContext';
import { t } from '@ardoq/i18n';
import {
  FormattingRuleLabel,
  InvertCheckbox,
  UnsupportedRuleContainer,
} from './atoms';
import {
  getSelectFieldOptions,
  QueryBuilderFilterType,
  RuleNotificationType,
} from '@ardoq/query-builder';
import type { ComponentOrReferenceFormattingRule } from '../types';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { WarningNotificationRule } from '../../../WarningNotificationRule';
import { perspectivesRowDragAndDropHandlers } from '../../../perspectivesRowDragAndDropHandlers';
import { FormattingRuleRowByComponentOrReferenceBody } from './FormattingRuleRowByComponentOrReferenceBody';
import { ListFieldFormattingRuleBody } from './ListFieldFormattingRuleBody';
import { FORMATTING_RULE_LINE_HEIGHT as LINE_HEIGHT } from '../../../consts';
import { FormattingRuleTypePicker } from './FormattingRuleTypePicker';
import { FilterInfoOperator, FormattingRuleOperator } from '@ardoq/api-types';
import { CommonDefaultFilterKeys } from '@ardoq/api-types';
import {
  GridFormattingRuleRow,
  GridFullWidthCell,
  GroupBox,
  InputsWrapper,
} from '../../../atoms';
import { ColorRangeFormattingRuleBody } from './ColorRangeFormattingRuleBody';
import styled from 'styled-components';
import { isRuleVersionSupported } from '../../../utils';
import { canSetTypeField, getValueForTypeField } from '../utils';

const ListFieldFormattingValueCell = styled(InputsWrapper)`
  grid-column: 6 / 7;
`;

type NewFormattingRuleRowByComponentOrReferenceProperties = {
  formattingRule: ComponentOrReferenceFormattingRule;
};

export const NewFormattingRuleRowByComponentOrReference = ({
  formattingRule: rule,
}: NewFormattingRuleRowByComponentOrReferenceProperties) => {
  const formattingContext = useFormattingContext();
  const {
    updateRule,
    removeRule,
    getRuleNotificationParams,
    ruleInfo,
    i18nLocale,
  } = formattingContext;

  const { __id: ruleId, field, comparator } = rule;
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(ruleId, useDragAndDropContext());

  const info = ruleInfo[ruleId];
  const filter = field ? info?.fieldFormatting?.[field] : null;

  if (!info || (field && !filter)) {
    const {
      message = t(
        i18nLocale,
        'perspectives:formattingRuleRow.errorCouldNotLoad',
        { field },
        'Error - could not load formatting rule "{{field}}"'
      ),
      notificationType,
    } = getRuleNotificationParams?.(rule) || {};
    const isWarning = notificationType === RuleNotificationType.WARNING;

    if (!isWarning) {
      return <GroupBox>{message}</GroupBox>;
    }

    return (
      <WarningNotificationRule
        message={message}
        removeRule={() => removeRule(rule.__id)}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        dataTestId="group-box-formatting-warning-notification"
      />
    );
  }

  const maybeFieldLabel = field ? filter?.label : undefined;

  const { fieldFormatting, filterGroups: formattingGroups, comparators } = info;
  /** Does this formatting rule represent a list field? */
  const isListFieldFormatting =
    filter?.filterType === QueryBuilderFilterType.SELECT &&
    comparator === FilterInfoOperator.EQUALS &&
    !isNil(rule.value);

  const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

  return (
    <GridFormattingRuleRow
      data-testid={`group-box-formatting-rule-by-${rule.type}`}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <DragButton
        draggable={true}
        style={{ lineHeight: `${LINE_HEIGHT}px` }}
        i18nLocale={i18nLocale}
      />
      <FormattingRuleLabel
        ruleType={rule.type}
        label={maybeFieldLabel}
        isDisabled={isRuleUnsupported}
      />
      <InputsWrapper $isDisabled={isRuleUnsupported}>
        <FormattingRuleTypePicker
          rule={rule}
          formattingContext={formattingContext}
        />
      </InputsWrapper>
      <InputsWrapper $isDisabled={isRuleUnsupported}>
        <Select
          aria-label={t(
            i18nLocale,
            'perspectives:formattingRuleRow.attribute',
            'Attribute'
          )}
          dataTestId="formatting-rule-field-select"
          placeholder={t(
            i18nLocale,
            'perspectives:formattingRuleRow.searchAndSelect',
            'Search and select'
          )}
          value={rule.field}
          options={getSelectFieldOptions(formattingGroups ?? [])}
          onValueChange={field => {
            if (typeof field !== 'string') return; // shouldn't happen
            const filter = fieldFormatting[field];
            if (!filter) return; // shouldn't happen

            const value =
              field === CommonDefaultFilterKeys.TYPE
                ? getValueForTypeField(rule.typeNames)
                : null;
            if (!canSetTypeField(field, rule.typeNames)) return;
            updateRule(ruleId, {
              field,
              comparator: filter.comparators[0],
              value,
            });
          }}
        />
      </InputsWrapper>
      {filter && isListFieldFormatting ? (
        <ListFieldFormattingValueCell $isDisabled={isRuleUnsupported}>
          <ListFieldFormattingRuleBody
            rule={rule}
            filter={filter}
            formattingContext={formattingContext}
          />
        </ListFieldFormattingValueCell>
      ) : filter && rule.comparator === FormattingRuleOperator.COLOR_RANGE ? (
        <ColorRangeFormattingRuleBody
          formattingContext={formattingContext}
          rule={rule}
        />
      ) : filter ? (
        <FormattingRuleRowByComponentOrReferenceBody
          rule={rule}
          filter={filter}
          comparators={comparators}
          formattingContext={formattingContext}
          isNewConditionalFormatting={true}
        />
      ) : (
        <>
          <div>&nbsp;</div>
          <div>&nbsp;</div>
        </>
      )}
      <GhostButton
        dataTestId="remove-formatting-rule-button"
        onClick={() => removeRule(ruleId)}
      >
        <DeleteIcon />
      </GhostButton>

      {rule.isInverted && (
        <GridFullWidthCell>
          <UnsupportedRuleContainer>
            <InvertCheckbox isInverted onChange={noop} />
          </UnsupportedRuleContainer>
        </GridFullWidthCell>
      )}
    </GridFormattingRuleRow>
  );
};
